import request from '../utils/request'
import type { ApiResult } from '../utils/request'

export interface SysDict {
  id: string | number
  dictName: string
  dictCode: string
  status: number
  remark?: string
}

export interface SysDictItem {
  id?: string | number
  dictCode: string
  itemLabel: string
  itemValue: string
  sortOrder: number
  isDefault?: number
  status?: number
  remark?: string
  extConfig?: Record<string, any>
  value: string
  label: string
}

const omitUndefined = (payload: Record<string, any>) => {
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) delete payload[key]
  })
  return payload
}

const parseExtConfig = (value: any): Record<string, any> => {
  if (!value) return {}
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

const normalizeSysDict = (raw: any): SysDict => ({
  id: raw.id,
  dictName: raw.dictName ?? raw.dict_name ?? '',
  dictCode: raw.dictCode ?? raw.dict_code ?? '',
  status: raw.status ?? 1,
  remark: raw.remark ?? '',
})

const normalizeDictItem = (raw: any, fallbackDictCode = ''): SysDictItem => {
  const itemValue = raw.itemValue ?? raw.item_value ?? raw.value ?? ''
  const itemLabel = raw.itemLabel ?? raw.item_label ?? raw.label ?? ''
  return {
    id: raw.id,
    dictCode: raw.dictCode ?? raw.dict_code ?? fallbackDictCode,
    itemLabel,
    itemValue,
    value: itemValue,
    label: itemLabel,
    sortOrder: raw.sortOrder ?? raw.sort_order ?? 0,
    isDefault: raw.isDefault ?? raw.is_default ?? 0,
    status: raw.status ?? 1,
    remark: raw.remark ?? '',
    extConfig: parseExtConfig(raw.extConfig ?? raw.ext_config),
  }
}

const toSysDictPayload = (data: Partial<SysDict>) => omitUndefined({
  id: data.id,
  dict_name: data.dictName ?? (data as any).dict_name,
  dict_code: data.dictCode ?? (data as any).dict_code,
  status: data.status,
  remark: data.remark,
})

const toDictItemPayload = (data: Record<string, any>) => omitUndefined({
  id: data.id,
  dict_code: data.dictCode ?? data.dict_code,
  item_label: data.itemLabel ?? data.item_label ?? data.label,
  item_value: data.itemValue ?? data.item_value ?? data.value,
  sort_order: data.sortOrder ?? data.sort_order,
  is_default: data.isDefault ?? data.is_default,
  status: data.status,
  remark: data.remark,
  ext_config: typeof data.extConfig === 'string'
    ? data.extConfig
    : data.extConfig
      ? JSON.stringify(data.extConfig)
      : data.ext_config,
})

/**
 * 获取系统字典数据
 */
export function getSysDictList() {
  return request.get<any, ApiResult<any[]>>('/base/sys-dict/list').then((res) => ({
    ...res,
    data: (res.data || []).map(normalizeSysDict),
  }))
}

export function saveSysDict(data: Partial<SysDict>) {
  return request.post<any, ApiResult<boolean>>('/base/sys-dict', toSysDictPayload(data))
}

/**
 * 单位换算
 */
export function getUnitConversionList() {
  return request.get<any, ApiResult<any[]>>('/base/unit-conversion/list')
}

// ===== 数据字典批量查询 =====

/** 批量查询字典项（前端初始化一次拉全部） */
export function getDictBatchItems(codes?: string[]) {
  const params: Record<string, string> = {}
  if (codes?.length) params.codes = codes.join(',')
  return request.get<any, ApiResult>('/base/sys-dict/batch-items', { params })
}

/** 按字典编码查询字典项 */
export function getDictItemsByCode(dictCode: string) {
  return request.get<any, ApiResult<any[]>>('/base/sys-dict/items', { params: { dictCode } }).then((res) => ({
    ...res,
    data: (res.data || []).map((item) => normalizeDictItem(item, dictCode)),
  }))
}

/** 新增/修改字典项 */
export function saveDictItem(data: {
  id?: number | string
  dictCode: string
  itemLabel: string
  itemValue: string
  sortOrder?: number
  isDefault?: number
  status?: number
  remark?: string
  extConfig?: string | Record<string, any>
}) {
  return request.post<any, ApiResult<boolean>>('/base/sys-dict/item', toDictItemPayload(data))
}

/** 删除字典项 */
export function deleteDictItem(id: number | string) {
  return request.delete<any, ApiResult<boolean>>(`/base/sys-dict/item/${id}`)
}
